/**
 * Stripe Service Layer
 * Trial management and Stripe configuration utilities.
 * For checkout and portal flows, use subscriptionService.ts instead.
 */
import { FREE_TRIAL_DAYS } from '@/lib/constants'

import { supabase } from '@/lib/supabase'
import type { SubscriptionTier, BillingInterval } from '@/lib/stripe'

export interface SubscriptionStatus {
  plan_tier: SubscriptionTier
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired'
  trial_ends_at: string | null
  current_period_start: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  stripe_customer_id: string | null
}

/**
 * Get current subscription status
 */
export async function getSubscriptionStatus(organizationId: string): Promise<SubscriptionStatus | null> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('org_id', organizationId)
    .single()

  if (error && error.code !== 'PGRST116') {
    console.error('Failed to get subscription:', error)
    return null
  }

  if (!data) return null

  return {
    plan_tier: data.plan_tier as SubscriptionTier,
    status: data.status,
    trial_ends_at: data.trial_ends_at,
    current_period_start: data.current_period_start,
    current_period_end: data.current_period_end,
    cancel_at_period_end: data.cancel_at_period_end,
    stripe_customer_id: data.stripe_customer_id,
  }
}

/**
 * Start a free trial for an organization
 */
export async function startFreeTrial(organizationId: string): Promise<void> {
  const trialEndsAt = new Date()
  trialEndsAt.setDate(trialEndsAt.getDate() + FREE_TRIAL_DAYS)

  const { error } = await supabase.from('subscriptions').upsert({
    org_id: organizationId,
    plan_tier: 'free_trial',
    status: 'trialing',
    trial_ends_at: trialEndsAt.toISOString(),
  }, { onConflict: 'org_id' })

  if (error) throw new Error(`Failed to start trial: ${error.message}`)

  // Sync tier to organizations (best-effort — constraint may reject 'free_trial' on older schemas)
  const { error: orgUpdateError } = await supabase.from('organizations').update({
    subscription_tier: 'free_trial',
  }).eq('id', organizationId)

  if (orgUpdateError) {
    console.warn('Could not sync subscription_tier to organizations:', orgUpdateError.code, orgUpdateError.message)
  }
}

/**
 * Check if Stripe is configured
 */
export function isStripeConfigured(): boolean {
  return !!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
}

export type { SubscriptionTier, BillingInterval }
